import React, { useEffect, useMemo, useState, useCallback } from 'react';
import {
    Box,
    Heading,
    Text,
    Spinner,
    Stack,
    Input,
    Flex,
    Button,
    useDisclosure,
    IconButton,
    Modal,
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalBody,
    ModalCloseButton,
    Collapse,
    useColorModeValue,
    chakra, List, ListItem,
} from '@chakra-ui/react';
import { EditIcon, DeleteIcon } from '@chakra-ui/icons';
import { motion } from 'framer-motion';
import { SupabaseClient } from '../../supabaseClient';
import { useAuth } from '../../contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import MealEntryForm from '../MealEntry/MealEntryForm';
import WeeklyAnalysis from "../../components/analysis/WeeklyAnalysis";
import { parse, format, subDays, isWithinInterval } from 'date-fns';
import i18n from 'i18next';
import {DRI} from "../../components/analysis/dri";
import {Cell, Legend, Pie, PieChart, ResponsiveContainer, Tooltip} from "recharts";

// Color palette for macro slices
const SLICE_COLORS = ['#4FD1C5', '#F6E05E', '#F56565']; // teal, yellow, red
// Motion wrappers
const MotionBox = motion(chakra.div);
const MotionHeading = motion(Heading);
motion(Text);
const groupByDate = logs =>
    logs.reduce((acc, entry) => {
        const key = entry.meal_date;
        if (!acc[key]) acc[key] = [];
        acc[key].push(entry);
        return acc;
    }, {});

const MealJournalPage = () => {
    const { t } = useTranslation();
    /* ------------------------------------------------------------------ */
    /*  THEME TOKEN HOOKS                                                 */
    /* ------------------------------------------------------------------ */
    const pageBg       = useColorModeValue('gray.50', 'gray.900');
    const cardBg       = useColorModeValue('white', 'gray.700');
    const accent       = useColorModeValue('teal.400', 'teal.300');
    const titleColor   = useColorModeValue('gray.800', 'white');
    const descColor    = useColorModeValue('gray.600', 'gray.300');
    const boxColor     = useColorModeValue('gray.200','gray.600');

    /* ------------------------------------------------------------------ */
    /*  STATE & DATA FETCHING                                             */
    /* ------------------------------------------------------------------ */
    const { user } = useAuth();
    const [mealLogs, setMealLogs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const today = format(new Date(), 'yyyy-MM-dd');
    const [selectedDate, setSelectedDate] = useState(today);

    const { isOpen, onOpen, onClose } = useDisclosure();
    const [entryToEdit, setEntryToEdit] = useState(null);

    // Single state for analysis text.
    const [analysis, setAnalysis] = useState(null);
    const [analyzing, setAnalyzing] = useState(false);

    // Track which macros panels are open
    const [openMacros, setOpenMacros] = useState({});
    const toggleMacros = id => setOpenMacros(prev => ({ ...prev, [id]: !prev[id] }));

    // Fetch meal logs
    const fetchMealLogs = useCallback(async () => {
        if (!user) return;
        setLoading(true);
        try {
            const { data, error } = await SupabaseClient
                .from('meal_logs')
                .select('*, meal_nutrients!meal_nutrients_meal_log_id_fkey(*)')
                .eq('profile_id', user.id)
                .order('meal_date', { ascending: false })
                .order('created_at', { ascending: false });
            if (error) throw error;
            setMealLogs(data.map(log => ({
                ...log,
                meal_nutrients: Array.isArray(log.meal_nutrients) ? log.meal_nutrients[0] : null,
            })));
        } catch (err) {
            setError(err.message || 'Failed to load logs');
        } finally {
            setLoading(false);
        }
    }, [user]);

    useEffect(() => { fetchMealLogs(); }, [fetchMealLogs]);

    // Fetch existing weekly analysis
    const fetchExistingAnalysis = useCallback(async () => {
        if (!user) return;
        try {
            const res = await fetch(
                `/api/fetchWeeklyNutrition?profileId=${user.id}&weekEnd=${selectedDate}`
            );
            if (!res.ok) throw new Error('Failed to fetch analysis');
            const { analysis } = await res.json();
            setAnalysis(analysis || null);
        } catch {
            setAnalysis(null);
        }
    }, [user, selectedDate]);

    useEffect(() => { fetchExistingAnalysis(); }, [fetchExistingAnalysis]);

    // Trigger analysis
    const handleAnalyze = async () => {
        setAnalyzing(true);
        try {
            const totals = logsToDisplay.reduce((acc, log) => {
                const n = log.meal_nutrients || {};
                Object.keys(acc).forEach(k => { acc[k] += n[k] || 0; });
                return acc;
            }, {
                calories:0, protein_g:0, carbs_g:0, fats_g:0,
                fiber_g:0, sugars_g:0, vitamin_a_mcg:0, vitamin_b1_mg:0,
                vitamin_b2_mg:0, vitamin_b3_mg:0, vitamin_b5_mg:0, vitamin_b6_mg:0,
                vitamin_b7_mcg:0, vitamin_b9_mcg:0, vitamin_b12_mcg:0, vitamin_c_mg:0,
                vitamin_d_mcg:0, vitamin_e_mg:0, vitamin_k_mcg:0, calcium_mg:0,
                iron_mg:0, magnesium_mg:0, phosphorus_mg:0, potassium_mg:0,
                sodium_mg:0, zinc_mg:0, iodine_mcg:0, selenium_mcg:0,
                copper_mg:0, manganese_mg:0, chromium_mcg:0, omega_3_mg:0,
                omega_6_mg:0, cholesterol_mg:0, creatine_mg:0
            });
            const res = await fetch('/api/analyzeWeeklyNutrition', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    totals,
                    weekEndDate: selectedDate,
                    profileId: user.id,
                    language: i18n.language,
                    numDates
                }),
            });
            const { analysis: newAnalysis } = await res.json();
            setAnalysis(newAnalysis);
        } catch {
            setAnalysis({strengths:[], deficits:[], recommendations:[]});
        } finally {
            setAnalyzing(false);
        }
    };

    // Current week logs
    const logsToDisplay = useMemo(() => {
        const end = parse(selectedDate, 'yyyy-MM-dd', new Date());
        const start = subDays(end, 6);
        return mealLogs.filter(entry =>
            isWithinInterval(parse(entry.meal_date,'yyyy-MM-dd',new Date()), { start, end })
        );
    }, [mealLogs, selectedDate]);

    const groupedLogs = useMemo(() => groupByDate(logsToDisplay), [logsToDisplay]);

    /* ---------- ANALYTICS MEMOS ---------- */

// totals for the current 7‑day window
    const numDates = new Set(logsToDisplay.map(item => item.meal_date)).size;
    const totals = useMemo(() => logsToDisplay.reduce((acc, log) => {
        const n = log.meal_nutrients ?? {};
        Object.keys(acc).forEach(k => { acc[k] += n[k] ?? 0; });
        return acc;
    }, {
        calories:0, protein_g:0, carbs_g:0, fats_g:0,
        fiber_g:0, sugars_g:0, vitamin_a_mcg:0, vitamin_b1_mg:0,
        vitamin_b2_mg:0, vitamin_b3_mg:0, vitamin_b5_mg:0, vitamin_b6_mg:0,
        vitamin_b7_mcg:0, vitamin_b9_mcg:0, vitamin_b12_mcg:0, vitamin_c_mg:0,
        vitamin_d_mcg:0, vitamin_e_mg:0, vitamin_k_mcg:0, calcium_mg:0,
        iron_mg:0, magnesium_mg:0, phosphorus_mg:0, potassium_mg:0,
        sodium_mg:0, zinc_mg:0, iodine_mcg:0, selenium_mcg:0,
        copper_mg:0, manganese_mg:0, chromium_mcg:0, omega_3_mg:0,
        omega_6_mg:0, cholesterol_mg:0, creatine_mg:0
    }), [logsToDisplay]);

// parse GPT text → three lists
    const strengths = analysis?.strengths
    const deficits = analysis?.deficits
    const recommendations = analysis?.recommendations

// micros for the nutrient table
    const microsArr = useMemo(
        () =>
            Object.entries(totals)
                .filter(([k]) => !["calories", "protein_g", "carbs_g", "fats_g"].includes(k))
                .map(([name, value]) => ({
                    name,
                    value,
                    dri: DRI[name] ?? null        // ⬅️ add the look‑up here
                })),
        [totals]
    );


    /* ------------------------------------------------------------------ */
    /*  RENDER                                                            */
    /* ------------------------------------------------------------------ */

    if (loading) return (
        <Box textAlign="center" mt={10} bg={pageBg} minH="100vh" p={8}>
            <Spinner size="xl" color={accent} />
            <Text mt={4} color={descColor}>{t('mealJournal.loading')}</Text>
        </Box>
    );
    if (error) return (
        <Box textAlign="center" mt={10} bg={pageBg} minH="100vh" p={8}>
            <Text color="red.500">{t('mealJournal.error')}: {error}</Text>
        </Box>
    );

    return (
        <Box as="main" bg={pageBg} minH="100vh" py={10} px={4}>
            {/* Title */}
            <MotionHeading textAlign="center" color={titleColor} fontSize={{ base:'3xl', md:'4xl' }} mb={8}
                           initial={{ opacity:0, y:-20 }} animate={{ opacity:1, y:0 }} transition={{ duration:0.6 }}>
                {t('mealJournal.title')}
            </MotionHeading>

            {/* Date selector + add meal */}
            <Flex mb={10} justify="space-between" align={{base:'stretch',md:'center'}} flexDir={{base:'column',md:'row'}} gap={4} maxW="3xl" mx="auto">
                <Box>
                    <Text mb={2} color={descColor}>{t('mealJournal.selectWeek')}</Text>
                    <Input type="date" value={selectedDate} onChange={e=>setSelectedDate(e.target.value)} max={today} bg={cardBg} borderColor={accent}/>
                </Box>
                <Button colorScheme="teal" onClick={() => { setEntryToEdit(null); onOpen(); }} alignSelf={{base:'flex-end',md:'auto'}}>
                    {t('mealJournal.addMeal')}
                </Button>
            </Flex>

            {/* Weekly summary & analysis */}
            {logsToDisplay.length > 0 && (
                <MotionBox
                    bg={cardBg}
                    p={8}
                    rounded="2xl"
                    shadow="xl"
                    mb={12}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5 }}
                >
                    <WeeklyAnalysis
                        totals={totals}
                        numDates={numDates}
                        strengths={strengths}
                        deficits={deficits}
                        recommendations={recommendations}
                        micros={microsArr}
                    />

                    {/* Analyse / Re‑analyse */}
                    <Flex mt={6} gap={3} flexWrap="wrap">
                        <Button
                            colorScheme={analysis ? 'orange' : 'teal'}
                            onClick={handleAnalyze}
                            isLoading={analyzing}
                        >
                            {analysis
                                ? t('mealJournal.buttons.reAnalyze')
                                : t('mealJournal.buttons.analyze')}
                        </Button>
                    </Flex>
                </MotionBox>
            )}

            {/* Meal entries */}
            {Object.entries(groupedLogs).sort((a,b)=>b[0].localeCompare(a[0])).map(([date, entries])=>(
                <MotionBox key={date} bg={cardBg} p={8} rounded="2xl" shadow="xl" mb={10}
                           initial={{ opacity:0, y:20 }} whileInView={{ opacity:1, y:0 }} transition={{ duration:0.5 }}>
                    <Heading size="md" mb={6} color={titleColor}>{format(parse(date,'yyyy-MM-dd',new Date()),'MMMM d, yyyy')}</Heading>
                    <Stack spacing={6}>
                        {entries.map(entry=>{
                            const macros = entry.meal_nutrients
                            const pieData = macros ? [
                                { name: 'Protein', value: macros.protein_g },
                                { name: 'Carbs', value: macros.carbs_g },
                                { name: 'Fats', value: macros.fats_g }
                            ] : [];
                            return (
                                <Box key={entry.id} p={6} bg={pageBg} borderRadius="lg" border="1px solid" borderColor={boxColor}>
                                    <Flex justify="space-between" align="flex-start" flexWrap="wrap" gap={4}>
                                        <Box flex="1 1 60%">
                                            <Text fontWeight="bold" color={titleColor}>{entry.food_description}</Text>
                                            {entry.note && <Text mt={2} color={descColor}>{entry.note}</Text>}
                                        </Box>
                                        <Stack direction="row" spacing={2}>
                                            <IconButton size="sm" icon={<EditIcon />} aria-label={t('mealJournal.modal.edit')} onClick={()=>{setEntryToEdit(entry);onOpen();}}/>
                                            <IconButton size="sm" icon={<DeleteIcon />} aria-label={t('mealJournal.deleteAria')} onClick={async()=>{await SupabaseClient.from('meal_logs').delete().eq('id',entry.id);setMealLogs(prev=>prev.filter(e=>e.id!==entry.id));}}/>
                                            <Button size="sm" variant="outline" onClick={()=>toggleMacros(entry.id)}>
                                                {openMacros[entry.id] ? t('mealJournal.buttons.hideMacros') : t('mealJournal.buttons.showMacros')}
                                            </Button>
                                        </Stack>
                                    </Flex>
                                    <Collapse in={openMacros[entry.id]} animateOpacity>
                                        <Flex direction={{ base:'column', md:'row' }} align="center" justify="space-between" mt={6} gap={6}>
                                            <Box flex="1" minW="200px" h="200px">
                                                <ResponsiveContainer width="100%" height="100%">
                                                    <PieChart>
                                                        <Pie data={pieData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={80} label>
                                                            {pieData && pieData.map((_, i) => (
                                                                <Cell key={i} fill={SLICE_COLORS[i % SLICE_COLORS.length]} />
                                                            ))}
                                                        </Pie>
                                                        <Tooltip />
                                                        <Legend verticalAlign="bottom" height={36} />
                                                    </PieChart>
                                                </ResponsiveContainer>
                                            </Box>
                                            <Box flex="1" minW="220px">
                                                <List spacing={2} color={descColor}>
                                                    <ListItem><chakra.span fontWeight="semibold" color={titleColor}>Calories:</chakra.span> {macros?.calories ?? '-'} kcal</ListItem>
                                                    <ListItem><chakra.span fontWeight="semibold" color={titleColor}>Protein:</chakra.span> {macros?.protein_g ?? '-'} g</ListItem>
                                                    <ListItem><chakra.span fontWeight="semibold" color={titleColor}>Carbs:</chakra.span> {macros?.carbs_g ?? '-'} g</ListItem>
                                                    <ListItem><chakra.span fontWeight="semibold" color={titleColor}>Fats:</chakra.span> {macros?.fats_g ?? '-'} g</ListItem>
                                                </List>
                                            </Box>
                                        </Flex>
                                    </Collapse>
                                </Box>
                            );
                        })}
                    </Stack>
                </MotionBox>
            ))}

            {/* Modal */}
            <Modal isOpen={isOpen} onClose={onClose} size="xl">
                <ModalOverlay />
                <ModalContent bg={cardBg}>
                    <ModalHeader>{entryToEdit ? t('mealJournal.modal.edit') : t('mealJournal.modal.add')}</ModalHeader>
                    <ModalCloseButton />
                    <ModalBody pb={6}>
                        <MealEntryForm initialEntry={entryToEdit} onSuccess={()=>{fetchMealLogs();onClose();}}/>
                    </ModalBody>
                </ModalContent>
            </Modal>
        </Box>
    );
};

export default MealJournalPage;